import { useEffect, useState } from 'react';
import type { BlogPostPublic } from '../types/blog.types';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Collapse } from 'antd';
import { Link as RouterLink } from 'react-router-dom';
import {
  ClockCircleOutlined,
  LinkedinFilled,
  FacebookFilled,
  WhatsAppOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { getDefaultRouteForRole } from '../utils/permissions';
import { CookieConsent } from '../components/common/CookieConsent';
import { LandingNav } from '../components/landing/LandingNav';
import { LandingFooter } from '../components/landing/LandingFooter';
import { LandingCta } from '../components/landing/LandingCta';
import { SEO } from '../components/common/SEO';
import './Landing.css';

/**
 * Landing page showcasing Rongeka Africa
 * Provides options to login or create an account
 */
export const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [activeCard, setActiveCard] = useState(0);
  const location = useLocation();

  const heroContent = [
    {
      id: 0,
      title: 'School Management System for Zimbabwean Schools',
      subtitle: 'Students - Fees - Results',
      description: 'One secure platform for student management, fees, attendance, and more.',
      image: '/images/hero-students.jpg',
      primaryBtn: 'Login',
      primaryAction: () => navigate('/login'),
      secondaryBtn: 'Register',
      secondaryAction: () => navigate('/register'),
      showFreeBadge: true,
      badgeText: 'Free Trial...!'
    },
    {
      id: 1,
      title: '$0.50 Per Student Per Month',
      subtitle: 'ONE FULL TERM FREE',
      description: 'No setup fees. No hidden costs. Just $0.50 per student per month after your free trial.',
      image: '/images/hero-pricing.jpg',
      primaryBtn: 'Start Free Trial',
      primaryAction: () => navigate('/get-started'),
      secondaryBtn: 'View Pricing',
      secondaryAction: () => document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' }),
      showFreeBadge: true,
      badgeText: 'Free Trial...!'
    },
    {
      id: 2,
      title: 'Manage Your School on WhatsApp',
      subtitle: 'No App to Download - Parents Already Have It',
      description: 'Parents check results, attendance and fees. Staff access their portal. All from WhatsApp.',
      image: '/images/hero-messaging.jpg',
      primaryBtn: 'Start on WhatsApp',
      primaryAction: () => window.open('https://wa.me/263789442202?text=hi', '_blank'),
      secondaryBtn: 'Get Started',
      secondaryAction: () => navigate('/get-started'),
      showFreeBadge: true,
      badgeText: 'Free Trial...!'
    }
  ];

  const featureHighlights = [
    {
      id: 0,
      title: 'RONGEKA AFRICA',
      subtitle: 'About Us'
    },
    {
      id: 1,
      title: 'PRICING',
      subtitle: 'One Term Free'
    },
    {
      id: 2,
      title: 'WHATSAPP',
      subtitle: 'On WhatsApp'
    }
  ];

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      const defaultRoute = getDefaultRouteForRole(user.role);
      navigate(defaultRoute, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Handle scrolling to section when navigating from other pages
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Handle feature menu clicks
  useEffect(() => {
    const menuItems = document.querySelectorAll('.landing-feature-menu-item');
    
    menuItems.forEach((item) => {
      item.addEventListener('click', () => {
        const featureId = item.getAttribute('data-feature');
        
        // Update active menu item
        menuItems.forEach((i) => i.classList.remove('active'));
        item.classList.add('active');
        
        // Update active feature card
        const cards = document.querySelectorAll('.landing-feature-card');
        cards.forEach((card) => {
          if (card.getAttribute('data-feature') === featureId) {
            card.classList.add('active');
          } else {
            card.classList.remove('active');
          }
        });
      });
    });
    
    return () => {
      menuItems.forEach((item) => {
        item.removeEventListener('click', () => {});
      });
    };
  }, []);

  return (
    <>
      <SEO />
      {/* Main Navigation - shared with news and feature pages */}
      <LandingNav onLanding={true} />

      <div className="landing-container">
        {/* Social Links */}
        <div className="landing-social-links">
          <div className="landing-social-text">OUR SOCIAL CHANNELS</div>
          <div className="landing-social-divider"></div>
          <a
            href="https://www.linkedin.com/company/ark-portal/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <LinkedinFilled />
          </a>
          <a
            href="https://www.facebook.com/arkportalzw"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FacebookFilled />
          </a>
        </div>

        {/* Hero Section */}
        <section className="landing-hero" id="hero">
          {/* Background Images */}
          {heroContent.map((content) => (
            <div
              key={content.id}
              className={`landing-hero-background ${activeCard === content.id ? 'active' : ''}`}
            >
              <img
                src={content.image}
                alt={content.title}
                loading={content.id === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))}

          <div className="landing-hero-content">
            <h1 className="landing-title">{heroContent[activeCard].title}</h1>
            <h2 className="landing-subtitle-hero">{heroContent[activeCard].subtitle}</h2>
            
            <p className="landing-subtitle">
              {heroContent[activeCard].description}
            </p>

            <div className="landing-cta">
              <Button
                type="primary"
                size="large"
                onClick={heroContent[activeCard].primaryAction}
                className="landing-btn landing-btn-primary"
                icon={activeCard === 2 ? <WhatsAppOutlined /> : undefined}
              >
                {heroContent[activeCard].primaryBtn}
              </Button>
              <div className="landing-register-wrapper">
                <Button 
                  size="large" 
                  onClick={heroContent[activeCard].secondaryAction}
                  className="landing-btn landing-btn-secondary"
                >
                  {heroContent[activeCard].secondaryBtn}
                </Button>
                {heroContent[activeCard].showFreeBadge && (
                  <span className="landing-free-badge">
                    {heroContent[activeCard].badgeText || 'Free Trial'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Feature Highlight Cards */}
          <div className="landing-thumbnail-gallery">
            {featureHighlights.map((feature) => (
              <div
                key={feature.id}
                className={`landing-product-card ${activeCard === feature.id ? 'active' : ''}`}
                onClick={() => setActiveCard(feature.id)}
              >
                <div className="landing-product-card-overlay">
                  <div className="landing-product-card-title">{feature.title}</div>
                  <div className="landing-product-card-subtitle">{feature.subtitle}</div>
                </div>
              </div>
            ))}
          </div>


        </section>

        {/* About Section */}
        <section id="features" className="landing-section landing-about-section">
          <div className="landing-section-content">
            <div className="landing-about-grid">
              <div className="landing-about-content-col">
                <div className="landing-section-label">WHO WE ARE</div>
                <h2 className="landing-about-heading">
                  School Management for Zimbabwe
                </h2>
                <p className="landing-about-body">
                  Zimbabwean schools deserve better than spreadsheets and paper registers. Rongeka puts students, fees, accounting, and report cards in one platform - on WhatsApp, where your parents and teachers already are.
                </p>
               
                <div className="landing-about-stats">
                  <div className="landing-about-stat">
                    <span className="landing-about-stat-number">$0.50</span>
                    <span className="landing-about-stat-label">per student / month</span>
                  </div>
                  <div className="landing-about-stat-divider" />
                  <div className="landing-about-stat">
                    <span className="landing-about-stat-number">1 Term</span>
                    <span className="landing-about-stat-label">completely free</span>
                  </div>
                  <div className="landing-about-stat-divider" />
                  <div className="landing-about-stat">
                    <span className="landing-about-stat-number">WhatsApp</span>
                    <span className="landing-about-stat-label">no app needed</span>
                  </div>
                </div>
              </div>
              <div className="landing-about-image-col">
                <picture>
                  <source srcSet="/rongeka_logo.webp" type="image/webp" />
                  <img
                    src="/rongeka_logo.png"
                    alt="Rongeka Africa"
                    className="landing-about-image"
                    loading="lazy"
                    width="385"
                    height="319"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* WhatsApp Feature Section */}
        <section id="whatsapp-access" className="landing-section landing-whatsapp-section">
          <div className="landing-section-content">
            <div className="landing-whatsapp-grid">
              <div className="landing-whatsapp-image-col">
                <img
                  src="/images/whatsapp-parent.jpg"
                  alt="Parent messaging school on WhatsApp"
                  className="landing-whatsapp-image"
                  loading="lazy"
                />
              </div>
              <div className="landing-whatsapp-content-col">
                <div className="landing-section-label">WHATSAPP FIRST</div>
                <h2 className="landing-whatsapp-heading">
                  Rongeka Africa is on WhatsApp. Parents, just say <em>Hi.</em>
                </h2>
                <p className="landing-whatsapp-body">
                  No app to download. No password to forget. Your parents, teachers, and drivers already have WhatsApp - Rongeka meets them right there. One message and they are in.
                </p>
                <div className="landing-whatsapp-roles">
                  <div className="landing-whatsapp-role">
                    <span className="landing-whatsapp-role-label">Parents</span>
                    <ul>
                      <li>Fee balances and invoices</li>
                      <li>Attendance alerts</li>
                      <li>Student results</li>
                      <li>Message the school</li>
                    </ul>
                  </div>
                  <div className="landing-whatsapp-role">
                    <span className="landing-whatsapp-role-label">Teachers</span>
                    <ul>
                      <li>Mark attendance</li>
                      <li>View classes and timetable</li>
                      <li>Access grades</li>
                    </ul>
                  </div>
                  <div className="landing-whatsapp-role">
                    <span className="landing-whatsapp-role-label">Drivers</span>
                    <ul>
                      <li>View daily routes</li>
                      <li>Daily check-ins</li>
                      <li>Report issues</li>
                    </ul>
                  </div>
                </div>
                <Button
                  type="primary"
                  size="large"
                  icon={<WhatsAppOutlined />}
                  onClick={() => window.open('https://wa.me/263789442202?text=hi', '_blank')}
                  className="landing-whatsapp-cta-btn"
                >
                  Say Hi on WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Add-ons Section */}
        <section id="addons" className="landing-section landing-addons-detail-section">
          <div className="landing-section-content">
            <div className="landing-section-label">FLEXIBLE AND MODULAR</div>
            <h2 className="landing-section-title">Add-ons - Only Pay for What You Need</h2>
            <p className="landing-section-subtitle">
              The core platform covers attendance, fees, grades, messaging, and reports for every school.
              Add-on modules extend the system for schools that need more - activated in your settings, billed monthly, cancelled any time.
            </p>

            <div className="landing-addons-rows">

              <div className="landing-addon-row">
                <div className="landing-addon-row-visual">
                  <img src="/images/addon-accounting.jpg" alt="Zimbabwean school accounting and financial management" className="landing-addon-row-image" loading="lazy" />
                </div>
                <div className="landing-addon-row-content">
                  <span className="landing-addon-row-tag">Accounting</span>
                  <h3 className="landing-addon-row-title">Replace Sage Pastel with one connected system</h3>
                  <p className="landing-addon-row-body">
                    Stop entering the same data twice. Rongeka automatically posts fee payments into your accounts, combining fee management and full accounting in one system.
                  </p>
                  <ul className="landing-addon-row-list">
                    <li><CheckCircleOutlined />Trial balance, income statement, and balance sheet</li>
                    <li><CheckCircleOutlined />Bank reconciliation against your payment records</li>
                    <li><CheckCircleOutlined />Expense tracking by department or cost centre</li>
                    <li><CheckCircleOutlined />End-of-year reports ready for your auditor or board</li>
                  </ul>
                </div>
              </div>

              <div className="landing-addon-divider" />

              <div className="landing-addon-row landing-addon-row-reverse">
                <div className="landing-addon-row-visual">
                  <img src="/images/addon-hr.jpg" alt="School HR management system" className="landing-addon-row-image" loading="lazy" />
                </div>
                <div className="landing-addon-row-content">
                  <span className="landing-addon-row-tag">Human Resources</span>
                  <h3 className="landing-addon-row-title">Staff records, leave, and payroll - all in one place</h3>
                  <p className="landing-addon-row-body">
                    Manage contracts, leave, payroll, and staff records in one place. Generate payslips, track NSSA and PAYE, and keep complete employment histories without a separate HR system.
                  </p>
                  <ul className="landing-addon-row-list">
                    <li><CheckCircleOutlined />Staff contracts and full employment records</li>
                    <li><CheckCircleOutlined />Leave balances and approval workflow</li>
                    <li><CheckCircleOutlined />Monthly payroll processing and payslip generation</li>
                    <li><CheckCircleOutlined />NSSA and PAYE deduction tracking</li>
                  </ul>
                </div>
              </div>

              <div className="landing-addon-divider" />

              <div className="landing-addon-row">
                <div className="landing-addon-row-visual">
                  <img src="/images/addon-stock.jpg" alt="School stock and inventory management" className="landing-addon-row-image" loading="lazy" />
                </div>
                <div className="landing-addon-row-content">
                  <span className="landing-addon-row-tag">Stock and Inventory</span>
                  <h3 className="landing-addon-row-title">Know exactly what your school owns and where it is</h3>
                  <p className="landing-addon-row-body">
                    Manage school inventory with ease. Track stock, record procurement and distributions, and receive low-stock alerts for uniforms, textbooks, stationery, and more.
                  </p>
                  <ul className="landing-addon-row-list">
                    <li><CheckCircleOutlined />Inventory catalogue organised by category</li>
                    <li><CheckCircleOutlined />Procurement and goods-received records</li>
                    <li><CheckCircleOutlined />Issues to students and departments</li>
                    <li><CheckCircleOutlined />Low-stock alerts and usage reports</li>
                  </ul>
                </div>
              </div>

              <div className="landing-addon-divider" />

              <div className="landing-addon-row landing-addon-row-reverse">
                <div className="landing-addon-row-visual">
                  <img src="/images/addon-library.jpg" alt="Zimbabwean school library" className="landing-addon-row-image" loading="lazy" />
                </div>
                <div className="landing-addon-row-content">
                  <span className="landing-addon-row-tag">Library</span>
                  <h3 className="landing-addon-row-title">A full library catalogue your students can search from WhatsApp</h3>
                  <p className="landing-addon-row-body">
                    Manage books, loans, returns, overdue fines, and digital collections, while letting students check availability from their portal.
                  </p>
                  <ul className="landing-addon-row-list">
                    <li><CheckCircleOutlined />Book catalogue by ISBN or manual entry</li>
                    <li><CheckCircleOutlined />Borrowing, returns, and overdue tracking</li>
                    <li><CheckCircleOutlined />Fine management</li>
                    <li><CheckCircleOutlined />Student portal availability check</li>
                  </ul>
                </div>
              </div>
            </div>
          
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="landing-section landing-faq-section">
          <div className="landing-section-content">
            <div className="landing-section-label">COMMON QUESTIONS</div>
            <h2 className="landing-faq-heading">Frequently Asked Questions</h2>
            <Collapse
              className="landing-faq-collapse"
              expandIconPosition="end"
              items={[
                {
                  key: '1',
                  forceRender: true,
                  label: 'How much does Rongeka cost?',
                  children: <p>$0.50 per student per month after one full free term. A school with 100 students pays $50/month. No setup fees, no contracts, no credit card to start.</p>
                },
                {
                  key: '2',
                  forceRender: true,
                  label: 'Do parents and teachers need to download an app?',
                  children: <p>No. Everything runs on WhatsApp - parents check fee balances, results, and attendance alerts on the number they already use every day.</p>
                },
                {
                  key: '3',
                  forceRender: true,
                  label: 'Can Rongeka replace Sage Pastel for school accounting?',
                  children: <p>Yes, with the Accounting add-on. It adds full double-entry accounting with trial balance, bank reconciliation, and year-end reports, billed monthly on top of the per-student price - and fee income posts into your books automatically.</p>
                },
                {
                  key: '4',
                  forceRender: true,
                  label: 'Does Rongeka support ZIMSEC and Cambridge grading?',
                  children: <p>Yes. Grading scales are configurable to ZIMSEC or Cambridge structures, and report cards go straight to parents on WhatsApp.</p>
                },
                {
                  key: '5',
                  forceRender: true,
                  label: 'How long does it take to set up?',
                  children: <p>Most schools are running within a day - import your students, add staff, set your fees, done.</p>
                }
              ]}
            />

            <p className="landing-faq-more">
              <RouterLink to="/faq">Read all frequently asked questions →</RouterLink>
            </p>

            <div className="landing-section-cta-wrap">
              <p className="landing-section-cta-text">Your first full term is free - no demo call, no card.</p>
              <Button
                type="primary"
                size="large"
                onClick={() => navigate('/get-started')}
                className="landing-pricing-cta"
              >
                <span>Get Started for Free</span>
                <span className="landing-pricing-cta-arrow">→</span>
              </Button>
            </div>
          </div>
        </section>

        {/* Latest News replaces the Legal section */}
        <LatestNewsSection />


         {/* Pricing Section */}
        <section id="pricing" className="landing-section landing-pricing-section">
          <div className="landing-section-content">
            <h2 className="landing-section-title">Simple, Transparent Pricing</h2>
            <p className="landing-section-subtitle">
              No hidden fees. No surprises. Just honest pricing.
            </p>

            <div className="landing-pricing-card">
              <h3 className="landing-pricing-title">Pay As You Grow</h3>
              
              <div className="landing-pricing-cards-container">
                <div className="landing-pricing-amount landing-pricing-main">
                  <span className="landing-pricing-currency">$</span>
                  <span className="landing-pricing-value">0.50</span>
                  <span className="landing-pricing-period">per student/month</span>
                  <p className="landing-pricing-features-text">All core features. Add-ons billed separately.</p>
                </div>
              </div>

              <p className="landing-pricing-note" style={{ marginTop: '32px' }}>
                For example: A school with 100 students pays only $50/month
              </p>
              
              <div className="landing-pricing-trial">
                <ClockCircleOutlined /> <strong>One Full Term FREE Trial</strong> - No demo request required
              </div>

              <Button 
                type="primary" 
                size="large" 
                block
                onClick={() => navigate('/get-started')}
                className="landing-pricing-cta"
              >
                <span>Get Started for Free</span>
                <span className="landing-pricing-cta-arrow">→</span>
              </Button>
            </div>
          </div>
        </section>


        {/* Footer */}
        <LandingFooter />

        <CookieConsent />
      </div>
    </>
  );
};

// LatestNewsSection - fetches and renders the 3 most recent published blog posts
const LatestNewsSection: React.FC = () => {
  const [posts, setPosts] = useState<BlogPostPublic[]>([]);

  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
    fetch(`${API_BASE}/api/BlogPost/public?page=1&pageSize=3`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.data?.length) setPosts(data.data);
      })
      .catch(() => {});
  }, []);

  if (!posts.length) return null;

  return (
    <section className="landing-section landing-news-section">
      <div className="landing-section-content">
        <h2 className="landing-section-title" style={{ color: 'white' }}>Rongeka Africa News</h2>
        <p className="landing-section-subtitle" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          Stay updated with our latest insights and announcements
        </p>
        
        <div className="landing-news-grid">
          {posts.map((post) => (
            <RouterLink
              key={post.id}
              to={`/news/${post.slug}`}
              className="landing-news-card"
            >
              <div className="landing-news-meta">
                <span className="landing-news-date">
                  {new Date(post.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="landing-news-author">By {post.author || 'Rongeka Team'}</span>
              </div>
              <h3 className="landing-news-title">{post.title}</h3>
              {post.brief && (
                <p className="landing-news-description">
                  {post.brief.length > 120 ? post.brief.slice(0, 120) + '…' : post.brief}
                </p>
              )}
              {post.tags && post.tags.length > 0 && (
                <div className="landing-news-tags">
                  {post.tags.slice(0, 3).map((tag: string, idx: number) => (
                    <span key={idx} className="landing-news-tag">{tag}</span>
                  ))}
                </div>
              )}
              <span className="landing-news-read-more">Read More →</span>
            </RouterLink>
          ))}
        </div>
        
        <div className="text-center mt-3">
          <RouterLink to="/news">
            <Button size="large" className="landing-news-btn">
              View All Articles
            </Button>
          </RouterLink>
        </div>

        <LandingCta />
      </div>
    </section>
  );
};
